import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, X, MessageCircle, Phone, PhoneOff, Mic, MicOff } from "lucide-react";
import { toast } from "sonner";
import { Capacitor } from '@capacitor/core';

// Get backend URL - handles web, PWA, and native Capacitor
const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  if (Capacitor.isNativePlatform()) {
    return 'https://bizilgo.com';
  }
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    if (protocol === 'capacitor:' || protocol === 'file:' || protocol === 'ionic:') {
      return 'https://bizilgo.com';
    }
    if (protocol === 'https:' || protocol === 'http:') {
      return window.location.origin;
    }
  }
  return 'https://bizilgo.com';
};

const API = getBackendUrl() + "/api";

// ===== IN-APP CHAT COMPONENT =====
export const RideChat = ({ rideId, userId, userRole, otherPartyName, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/chat/${rideId}`);
      setMessages(res.data);
      
      // Mark messages as read
      await axios.put(`${API}/chat/${rideId}/mark-read?reader_role=${userRole}`);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    }
  };

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 3 seconds
    pollIntervalRef.current = setInterval(fetchMessages, 3000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [rideId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API}/chat/send`, {
        ride_id: rideId,
        sender_id: userId,
        sender_role: userRole,
        message: newMessage.trim()
      });
      setNewMessage("");
      fetchMessages();
    } catch (err) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col" data-testid="ride-chat">
      {/* Header */}
      <header className="bg-card p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">{otherPartyName}</h2>
            <p className="text-xs text-muted-foreground">
              {userRole === "customer" ? "Driver" : "Customer"}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-chat-btn">
          <X className="w-5 h-5" />
        </Button>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No messages yet</p>
            <p className="text-sm">Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.sender_role === userRole;
            return (
              <div
                key={msg.id}
                className={`flex ${isMe ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                    isMe
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-muted text-foreground rounded-bl-sm"
                  }`}
                >
                  <p className="text-sm">{msg.message}</p>
                  <p className={`text-xs mt-1 ${isMe ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {new Date(msg.timestamp).toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="p-4 bg-card border-t border-border">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1"
            disabled={sending}
            data-testid="chat-input"
          />
          <Button type="submit" disabled={sending || !newMessage.trim()} data-testid="send-message-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};

// ===== IN-APP CALL COMPONENT (WebRTC) =====
export const RideCall = ({ rideId, userId, userRole, otherPartyName, onClose, isIncoming = false, incomingCallId = null }) => {
  const [callStatus, setCallStatus] = useState(isIncoming ? "incoming" : "calling");
  const [callId, setCallId] = useState(incomingCallId);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const remoteAudioRef = useRef(null);

  const iceServers = {
    iceServers: [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" }
    ]
  };

  // Initialize WebRTC
  const initializeCall = async () => {
    try {
      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;

      // Create peer connection
      const pc = new RTCPeerConnection(iceServers);
      peerConnectionRef.current = pc;

      // Add local tracks
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      // Handle remote stream
      pc.ontrack = (event) => {
        remoteStreamRef.current = event.streams[0];
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = event.streams[0];
        }
      };

      // Handle ICE candidates
      pc.onicecandidate = async (event) => {
        if (event.candidate && callId) {
          await axios.post(`${API}/call/${callId}/signal`, {
            ride_id: rideId,
            caller_id: userId,
            caller_role: userRole,
            signal_type: "ice-candidate",
            signal_data: JSON.stringify(event.candidate)
          });
        }
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "connected") {
          setCallStatus("connected");
          startDurationTimer();
        } else if (pc.connectionState === "disconnected" || pc.connectionState === "failed") {
          endCall();
        }
      };

      return pc;
    } catch (err) {
      console.error("Failed to initialize call:", err);
      toast.error("Failed to access microphone");
      onClose();
      return null;
    }
  };

  // Start call (caller)
  const startCall = async () => {
    try {
      // Initiate call on backend
      const res = await axios.post(`${API}/call/initiate?ride_id=${rideId}&caller_id=${userId}&caller_role=${userRole}`);
      setCallId(res.data.call_id);

      const pc = await initializeCall();
      if (!pc) return;

      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      await axios.post(`${API}/call/${res.data.call_id}/signal`, {
        ride_id: rideId,
        caller_id: userId,
        caller_role: userRole,
        signal_type: "offer",
        signal_data: JSON.stringify(offer)
      });

      // Start polling for answer
      startPolling(res.data.call_id);
    } catch (err) {
      console.error("Failed to start call:", err);
      toast.error(err.response?.data?.detail || "Failed to start call");
      onClose();
    }
  };

  // Answer call (receiver)
  const answerCall = async () => {
    try {
      setCallStatus("connecting");
      
      const pc = await initializeCall();
      if (!pc) return;

      // Get call data with offer
      const callData = await axios.get(`${API}/call/${callId}`);
      
      if (callData.data.offer_sdp) {
        const offer = JSON.parse(callData.data.offer_sdp);
        await pc.setRemoteDescription(new RTCSessionDescription(offer));

        // Create and send answer
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        await axios.post(`${API}/call/${callId}/signal`, {
          ride_id: rideId,
          caller_id: userId,
          caller_role: userRole,
          signal_type: "answer",
          signal_data: JSON.stringify(answer)
        });

        // Add any ICE candidates
        for (const candidateStr of callData.data.ice_candidates_caller || []) {
          const candidate = JSON.parse(candidateStr);
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        }
      }

      startPolling(callId);
    } catch (err) {
      console.error("Failed to answer call:", err);
      toast.error("Failed to connect call");
      onClose();
    }
  };

  // Reject/End call
  const endCall = async () => {
    try {
      if (callId) {
        await axios.post(`${API}/call/${callId}/signal`, {
          ride_id: rideId,
          caller_id: userId,
          caller_role: userRole,
          signal_type: callStatus === "incoming" ? "reject" : "end"
        });
      }
    } catch (err) {
      console.error("Error ending call:", err);
    }

    // Cleanup
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }

    onClose();
  };

  // Poll for call updates
  const startPolling = (cId) => {
    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/call/${cId}`);
        const call = res.data;

        if (call.status === "ended" || call.status === "rejected" || call.status === "missed") {
          endCall();
          return;
        }

        // If we're the caller and got an answer
        if (callStatus === "calling" && call.answer_sdp && peerConnectionRef.current) {
          const answer = JSON.parse(call.answer_sdp);
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
          setCallStatus("connected");
          startDurationTimer();

          // Add ICE candidates from receiver
          for (const candidateStr of call.ice_candidates_receiver || []) {
            const candidate = JSON.parse(candidateStr);
            await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
          }
        }

        // Add new ICE candidates
        const candidates = userRole === call.caller_role 
          ? call.ice_candidates_receiver 
          : call.ice_candidates_caller;
        
        // Note: In production, track which candidates we've already added
      } catch (err) {
        console.error("Poll error:", err);
      }
    }, 1000);
  };

  const startDurationTimer = () => {
    durationIntervalRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = isMuted;
      });
      setIsMuted(!isMuted);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!isIncoming) {
      startCall();
    }

    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col items-center justify-center" data-testid="ride-call">
      {/* Hidden audio element for remote audio */}
      <audio ref={remoteAudioRef} autoPlay playsInline />

      {/* Call UI */}
      <div className="text-center text-white">
        {/* Avatar */}
        <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
          <Phone className={`w-10 h-10 text-primary ${callStatus === "calling" || callStatus === "incoming" ? "animate-pulse" : ""}`} />
        </div>

        {/* Name */}
        <h2 className="text-2xl font-bold mb-2">{otherPartyName}</h2>
        <p className="text-white/60 mb-8">
          {userRole === "customer" ? "Driver" : "Customer"}
        </p>

        {/* Status */}
        <p className="text-lg mb-8">
          {callStatus === "incoming" && "Incoming call..."}
          {callStatus === "calling" && "Calling..."}
          {callStatus === "connecting" && "Connecting..."}
          {callStatus === "connected" && formatDuration(callDuration)}
        </p>

        {/* Controls */}
        <div className="flex items-center justify-center gap-6">
          {callStatus === "incoming" ? (
            <>
              {/* Reject */}
              <button
                onClick={endCall}
                className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg"
                data-testid="reject-call-btn"
              >
                <PhoneOff className="w-7 h-7 text-white" />
              </button>
              {/* Answer */}
              <button
                onClick={answerCall}
                className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center shadow-lg"
                data-testid="answer-call-btn"
              >
                <Phone className="w-7 h-7 text-white" />
              </button>
            </>
          ) : (
            <>
              {/* Mute */}
              {callStatus === "connected" && (
                <button
                  onClick={toggleMute}
                  className={`w-14 h-14 rounded-full ${isMuted ? "bg-red-500" : "bg-white/20"} flex items-center justify-center`}
                  data-testid="mute-btn"
                >
                  {isMuted ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
                </button>
              )}
              {/* End Call */}
              <button
                onClick={endCall}
                className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg"
                data-testid="end-call-btn"
              >
                <PhoneOff className="w-7 h-7 text-white" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== CHAT BUTTON WITH UNREAD BADGE =====
export const ChatButton = ({ rideId, userRole, onClick }) => {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const checkUnread = async () => {
      try {
        const res = await axios.get(`${API}/chat/${rideId}/unread-count?user_role=${userRole}`);
        setUnreadCount(res.data.unread_count);
      } catch (err) {
        console.error(err);
      }
    };

    checkUnread();
    const interval = setInterval(checkUnread, 5000);
    return () => clearInterval(interval);
  }, [rideId, userRole]);

  return (
    <button
      onClick={onClick}
      className="relative w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-lg"
      data-testid="chat-button"
    >
      <MessageCircle className="w-6 h-6 text-white" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  );
};

// ===== INCOMING CALL CHECKER =====
export const IncomingCallChecker = ({ rideId, userRole, onIncomingCall }) => {
  useEffect(() => {
    const checkIncoming = async () => {
      try {
        const res = await axios.get(`${API}/call/incoming/${rideId}/${userRole}`);
        if (res.data.incoming_call) {
          onIncomingCall(res.data.incoming_call);
        }
      } catch (err) {
        console.error(err);
      }
    };

    const interval = setInterval(checkIncoming, 2000);
    return () => clearInterval(interval);
  }, [rideId, userRole, onIncomingCall]);

  return null;
};

export default RideChat;
